import { forwardRef, type InputHTMLAttributes, useId } from "react";

import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Input as ShadInput } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { cn } from "@src/shared/lib/utils";

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  containerClassName?: string;
}

/**
 * Labeled text input built on the shadcn Input + Label primitives. Keeps the
 * optional `label`, `hint`, and `error` fields that call-sites relied on.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      className,
      label,
      hint,
      error,
      required,
      containerClassName,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;

    return (
      <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
        {label ? (
          <Label htmlFor={inputId} className="text-sm text-foreground">
            {label}
            {required ? (
              <span className="ml-0.5 text-destructive" aria-hidden>
                *
              </span>
            ) : null}
          </Label>
        ) : null}
        <ShadInput
          id={inputId}
          ref={ref}
          required={required}
          aria-invalid={error ? "true" : undefined}
          className={cn(
            error && "border-destructive ring-destructive",
            className,
          )}
          {...props}
        />
        {error ? (
          <span className="text-xs text-destructive">{error}</span>
        ) : hint ? (
          <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </VerticalFlexbox>
    );
  },
);
Input.displayName = "Input";
